/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React from "react";
import { Tools } from "@/@corefabriccanvas";
import {
  CropIcon,
  EraserIcon,
  HandIcon,
  MarkerIcon,
  PenIcon,
  SelectIcon,
  SettingIcon,
  ShapesIcon,
  TrashIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "@/icons";
import PenSubTool from "./PenSubTool";
import EraserSubTool from "./EraserSubTool";
import ShapesSubTool from "./ShapeSubTool";
import { User } from "@/database/userDatabaseService";
import { DisplayTypes, SubToolType } from "@/types";
import { useTranslation } from 'react-i18next';
import { useModal } from "@/contexts/ModalContext";
import { CustomizeToolBox } from "../Settings/CustomizeToolBox";
import { AppDispatch } from "@/redux-store";

interface Tool {
  tool: Tools;
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  isActive: boolean;
  disabled?: boolean;
  subToolType?: SubToolType;
  subTool?: React.ReactNode;
}

interface MobileToolboxProps {
  displayType: string;
  user: User;
  currentTool: Tools;
  previousTool: Tools;
  dispatch: AppDispatch;
  setTool: (tool: Tools) => void;
  canvas: any;
  selectedColor: string;
  setSelectedColor: (color: string, userId: string) => void;
  thickness: number;
  setThickness: (thickness: number, userId: string) => void;
  eraseThickness: number;
  setEraserThickness: (thickness: number, userId: string) => void;
  updateActiveSubTool: (subTool: SubToolType | undefined) => void;
  activeSubTool: SubToolType | undefined;
  style?: React.CSSProperties;
  className?: string;
}

const MobileToolbox: React.FC<MobileToolboxProps> = ({
  displayType,
  user,
  currentTool,
  previousTool,
  dispatch,
  setTool,
  canvas,
  selectedColor,
  setSelectedColor,
  thickness,
  setThickness,
  eraseThickness,
  setEraserThickness,
  updateActiveSubTool,
  activeSubTool,
  style,
  className,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'tools' });
  const toolBoxPosition = "left";
  const {openModal, closeModal} = useModal();
  const zoom = (type: "zoomin" | "zoomout") => {
    if (canvas) {
      canvas.zoomInOut(type);
    }
  };

  const tools: Tool[] = [
    {
      tool: Tools.Pan,
      icon: <HandIcon className={undefined} />,
      title: "move",
      onClick: () => setTool(Tools.Pan),
      isActive: currentTool === Tools.Pan,
    },
    {
      tool: Tools.Select,
      icon: <SelectIcon className={undefined} />,
      title: "select",
      onClick: () => setTool(Tools.Select),
      isActive: currentTool === Tools.Select,
    },
    {
      tool: Tools.Pencil,
      icon: <PenIcon className={undefined} />,
      title: "pen",
      isActive: currentTool === Tools.Pencil,
      onClick: () => {
        setTool(Tools.Pencil);
        updateActiveSubTool(
          activeSubTool !== SubToolType.Pen ? SubToolType.Pen : undefined
        );
      },
      subToolType: SubToolType.Pen,
      subTool: (
        <PenSubTool
          selectedColor={selectedColor}
          selectedThickness={thickness}
          user={user}
          setSelectedColor={setSelectedColor}
          setThickness={setThickness}
          subtoolPosition={toolBoxPosition}
        />
      ),
    },
    {
      tool: Tools.Marker,
      icon: <MarkerIcon className={undefined} />,
      title: "marker",
      isActive: currentTool === Tools.Marker,
      onClick: () => {
        setTool(Tools.Marker);
        updateActiveSubTool(
          activeSubTool !== SubToolType.Marker ? SubToolType.Marker : undefined
        );
      },
      subToolType: SubToolType.Marker,
      subTool: (
        <PenSubTool
          selectedColor={selectedColor}
          selectedThickness={thickness}
          user={user}
          setSelectedColor={setSelectedColor}
          subtoolPosition={toolBoxPosition}
          setThickness={setThickness}
        />
      ),
    },
    {
      tool: Tools.Eraser,
      icon: <EraserIcon className={undefined} />,
      title: "eraser",
      isActive: currentTool === Tools.Eraser,
      onClick: () => {
        setTool(Tools.Eraser);
        updateActiveSubTool(
          activeSubTool !== SubToolType.Eraser ? SubToolType.Eraser : undefined
        );
      },
      subToolType: SubToolType.Eraser,
      subTool: (
        <EraserSubTool
          user={user}
          thickness={eraseThickness}
          previousTool={previousTool}
          dispatch={dispatch}
          setThickness={setEraserThickness}
          subToolPosition={toolBoxPosition}
          setTool={setTool}
          canvas={canvas}
        />
      ),
    },
    {
      tool: Tools.Crop,
      icon: <CropIcon className={undefined} />,
      title: "cut",
      isActive: currentTool === Tools.Crop,
      disabled:
        displayType === DisplayTypes.BlankPage ||
        displayType === DisplayTypes.PageItem,
      onClick: () => setTool(Tools.Crop),
    },
    {
      tool: Tools.Shapes,
      icon: <ShapesIcon className={undefined} />,
      isActive:
        currentTool === Tools.Line ||
        currentTool === Tools.DashedLine ||
        currentTool === Tools.Arrow ||
        currentTool === Tools.Circle ||
        currentTool === Tools.Polygon ||
        currentTool === Tools.Rectangle ||
        currentTool === Tools.Triangle ||
        currentTool === Tools.Text,
      title: "shapes",
      onClick: () => {
        updateActiveSubTool(
          activeSubTool !== SubToolType.Shapes ? SubToolType.Shapes : undefined
        );
      },
      subToolType: SubToolType.Shapes,
      subTool: (
        <ShapesSubTool setTool={setTool} subtoolPosition={toolBoxPosition} />
      ),
    },
    {
      tool: Tools.Trash,
      icon: <TrashIcon className={undefined} color={undefined} />,
      title: "clear_drawings",
      onClick: () => canvas.clear(),
      isActive: false,
    },
    {
      tool: Tools.ZoomIn,
      icon: <ZoomInIcon className={undefined} />,
      title: "zoom_in",
      onClick: () => zoom("zoomin"),
      isActive: false,
    },
    {
      tool: Tools.ZoomOut,
      icon: <ZoomOutIcon className={undefined} />,
      title: "zoom_out",
      onClick: () => zoom("zoomout"),
      isActive: false,
    },
  ];

  // Determine which tools to display based on whether test tools are visible
  const visibleTools = tools;


    const openCustomizeToolBox = () => {
      openModal(
        "test",
        t("customize_toolbox"),
        "withOverlay",
        <CustomizeToolBox
          userId={user.id}
          settings={user.userSettings}
          userTools={user.userSettings.selectedTools}
          closeModal={closeModal}
          dispatch={dispatch}
        />
      );
    };

  return (
    <div className={`tool-box-mobile ${className || ''}`} style={{zIndex: 3000, ...style}}>
      <div className="tools-area horizontal">
        {visibleTools.map((tool, index) => {
          if (user.userSettings?.selectedTools.includes(tool.tool)) {
            return (
              <div
                key={`tool-${index}`}
                onClick={tool.onClick}
                className={tool.isActive ? `tool-btn active` : "tool-btn"}
              >
                {tool.icon}
                {tool.subToolType && activeSubTool === tool.subToolType && (
                  <div className="mobile-sub-tool-wrapper">{tool.subTool}</div>
                )}
                <span>{t(tool.title)}</span>
              </div>
            );
          }
        })}
        <div className={"tool-btn"} onClick={openCustomizeToolBox}>
          <SettingIcon color={"#fff"} className={undefined} />
        </div>
      </div>
    </div>
  );
};

export default MobileToolbox;
